// Lote G (M51): PO 21, procedimiento de adquisición de EPP y ropa de trabajo con la matriz de EPP por puesto.
// Reglas DECIDIDAS POR CLAUDE, por validar con Kevin (plazos sugeridos por StorSafe, no normativos):
//  - Cumple: el procedimiento tiene una versión aprobada y firmada de los últimos 12 meses y cada puesto con
//    trabajadores activos (activo o con licencia) tiene al menos una fila en la matriz.
//  - En proceso: falta poco: los puestos sin matriz se crearon hace 30 días o menos, o el procedimiento tiene un borrador
//    sin aprobar (y no falta la matriz de ningún puesto con más de 30 días).
//  - Igual que los procedimientos de GT 37 (M49): el borrador va en epp_procedures y cada versión aprobada en
//    sst_document_versions (tipo procedimiento_epp), firmada por el responsable de SSO en pantalla o en papel; la
//    aprobada no se edita ni se borra (para cambiarla se aprueba una versión nueva desde el borrador).
//  - No cumple: los demás casos (puestos con trabajadores sin matriz desde hace más de 30 días, o sin versión aprobada
//    vigente ni borrador).
//  - Sin usar el módulo (ni versiones ni matriz): no se escribe nada; la traza dice cómo se cumple.
//  - Lo manual nunca se sobrescribe y un "No aplica" no se toca (mismo patrón que los programas anuales, lote C).
// La matriz no copia datos del puesto: guarda solo su id (el nombre se lee de Puestos de Trabajo). Las sugerencias
// salen del EPP que ya registran las evaluaciones de riesgos (IPER) del puesto: el usuario las confirma.

import { supabase } from '../../supabaseClient';
import type { Traza } from '../pe/pe-tipos';
import { esManual, type EstadoGuardadoChecklist, type EstadoItem, type TrazaItemPlan } from '../plan-emergencias-compliance-sync';
import { canonico } from '../firmas/firmas-logica';
import { diaEc, fechaEc } from '../fechas';
import { sha256Hex } from '../psicosocial-codigos';
import { ESTADOS_REGISTRADOS } from '../trabajadores-registrados';
import { plural } from '../plurales';

export const ITEM_PO21 = 'PO-21';
export const FUENTE_EPP_PO21 = 'epp_po21';
export const MODULO_PO21 = 'EPP (matriz por puesto y procedimiento)';
export const MESES_VIGENCIA_PROCEDIMIENTO = 12;
export const DIAS_GRACIA_PUESTO_NUEVO = 30;
export const NOTA_PLAZOS_PO21 = 'Vigencia de 12 meses y 30 días para los puestos nuevos: sugeridos por StorSafe, no normativos.';
const NORMA_PO21 = 'Decisión 584 Art. 11; DE 255 (2024) Art. 56';
const FUENTE_DOC = 'Anexo 1, Lista de verificación de cumplimiento de obligaciones de seguridad y salud en el trabajo (Ministerio del Trabajo)';

// ── Tipos ──
/** Borrador del procedimiento (epp_procedures, uno por empresa). */
export interface FilaProcedimientoEpp { id: string; company_id: string; body: string; created_at: string; updated_at: string }
/** Lo que el cálculo necesita de una versión aprobada (sst_document_versions, tipo procedimiento_epp). */
export interface VersionEppMin { version: number; approved_at: string }

export interface FilaMatriz {
  id: string;
  company_id: string;
  position_id: string;
  epp_id: string;
  specification: string | null;
  replacement_months: number | null;
  observation: string | null;
  origin: 'manual' | 'sugerencia';
  updated_at?: string;
}
/** Lo que se guarda de una fila: solo el id del puesto y del EPP, sin datos del puesto (nombre, descripción, riesgos). */
export type FilaMatrizNueva = Pick<FilaMatriz, 'company_id' | 'position_id' | 'epp_id' | 'specification' | 'replacement_months' | 'observation' | 'origin'>;

export interface PuestoMin { id: string; name: string; created_at: string | null }
export interface EppMin { id: string; name: string; category: string | null; lifespan_months: number | null }
export interface TrabajadorMin { position_id: string | null; status: string | null }
export interface IperEppMin { position_id: string | null; medida_epp: string | null }

// ── Texto base del procedimiento (editable) ──
export const TEXTO_BASE_PROCEDIMIENTO = `1. Objetivo
Asegurar que cada trabajador reciba el equipo de protección personal (EPP) y la ropa de trabajo que su puesto requiere, adecuados al riesgo y en buen estado.

2. Alcance
Todos los puestos de trabajo de la empresa y todo el EPP y la ropa de trabajo que se compra, entrega, repone o da de baja.

3. Responsables
- Responsable de SSO: mantiene la matriz de EPP por puesto, define las especificaciones y verifica lo recibido.
- Compras: cotiza y compra a proveedores que cumplan las especificaciones.
- Jefes de área: solicitan la reposición y controlan el uso.
- Trabajadores: usan, cuidan y devuelven el EPP según lo indicado.

4. Selección según la matriz
El EPP se selecciona con la matriz de EPP por puesto (anexa), que parte de la identificación de riesgos de cada puesto. Para cada EPP se indica la especificación o norma, la frecuencia de reposición y las observaciones.

5. Compra a proveedores
Se compra a proveedores que entreguen la ficha técnica y el certificado de conformidad del EPP con la especificación o norma de la matriz. Se conservan las cotizaciones y las facturas.

6. Verificación de lo recibido
Al recibir el EPP se verifica que coincide con lo solicitado (tipo, talla, especificación o norma, fecha de fabricación o caducidad) y que está en buen estado. Lo que no cumple se devuelve al proveedor.

7. Entrega con registro firmado
Cada entrega se registra con la fecha, el EPP entregado y la firma del trabajador que lo recibe. Junto con la entrega se capacita al trabajador en el uso, cuidado y limitaciones del EPP.

8. Reposición
El EPP se repone según la frecuencia de la matriz, o antes si se daña, se pierde o deja de proteger. El trabajador lo solicita a su jefe de área o al responsable de SSO.

9. Baja
El EPP dañado, vencido o contaminado se da de baja, se retira del uso y se dispone según su tipo. La baja se registra.

10. Revisión
Este procedimiento y la matriz se revisan al menos una vez al año, o antes si cambian los puestos, los riesgos o el EPP.`;

// ── Sugerencias desde las evaluaciones de riesgos del puesto ──
const normal = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

/** EPP del catálogo que las evaluaciones de riesgos del puesto ya registran (por nombre), sin los que ya están en la matriz. */
export function sugerenciasPuesto(puestoId: string, iper: IperEppMin[], catalogo: EppMin[], matriz: Pick<FilaMatriz, 'position_id' | 'epp_id'>[]): EppMin[] {
  const texto = normal(iper.filter(a => a.position_id === puestoId).map(a => a.medida_epp ?? '').join(' | '));
  if (!texto) return [];
  const ya = new Set(matriz.filter(m => m.position_id === puestoId).map(m => m.epp_id));
  return catalogo.filter(e => !ya.has(e.id) && normal(e.name).length >= 3 && texto.includes(normal(e.name)));
}

/** Fila a guardar: solo los ids y los datos propios de la matriz (la frecuencia parte de la vida útil del catálogo). */
export function filaMatrizNueva(companyId: string, puestoId: string, epp: Pick<EppMin, 'id' | 'lifespan_months'>, o: {
  specification?: string | null; replacement_months?: number | null; observation?: string | null; origin?: FilaMatriz['origin'];
} = {}): FilaMatrizNueva {
  const meses = o.replacement_months !== undefined ? o.replacement_months : epp.lifespan_months;
  return {
    company_id: companyId, position_id: puestoId, epp_id: epp.id,
    specification: (o.specification ?? '').trim() || null,
    replacement_months: meses && meses >= 1 && meses <= 120 ? Math.round(meses) : null,
    observation: (o.observation ?? '').trim() || null,
    origin: o.origin ?? 'manual',
  };
}

export const textoReposicion = (meses: number | null) => (meses ? `Cada ${plural(meses, 'mes', 'meses')}` : 'Sin frecuencia definida');

// ── Puestos con trabajadores y cobertura de la matriz ──
// created_at (instante) se pasa al día de Ecuador antes de contar días (lib/fechas).
const desdeIso = (iso: string) => { const [a, m, d] = diaEc(iso).split('-').map(Number); return new Date(a, m - 1, d); };
export function diasDesde(iso: string | null, hoy: Date): number {
  if (!iso) return Number.POSITIVE_INFINITY;
  const h = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  return Math.round((h.getTime() - desdeIso(iso).getTime()) / 86400000);
}
export function sumarMesesIso(iso: string, meses: number): string {
  const f = desdeIso(iso);
  const objetivo = new Date(f.getFullYear(), f.getMonth() + meses, 1);
  const ultimo = new Date(objetivo.getFullYear(), objetivo.getMonth() + 1, 0).getDate();
  objetivo.setDate(Math.min(f.getDate(), ultimo));
  return `${objetivo.getFullYear()}-${String(objetivo.getMonth() + 1).padStart(2, '0')}-${String(objetivo.getDate()).padStart(2, '0')}`;
}
const hoyIso = (hoy: Date) => `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;

export interface CoberturaPuesto { puesto: PuestoMin; trabajadores: number; filas: number; diasDesdeCreacion: number }
export function coberturaMatriz(puestos: PuestoMin[], trabajadores: TrabajadorMin[], matriz: Pick<FilaMatriz, 'position_id'>[], hoy: Date): CoberturaPuesto[] {
  return puestos.map(p => ({
    puesto: p,
    trabajadores: trabajadores.filter(w => w.position_id === p.id && (ESTADOS_REGISTRADOS as readonly string[]).includes(w.status ?? '')).length,
    filas: matriz.filter(m => m.position_id === p.id).length,
    diasDesdeCreacion: diasDesde(p.created_at, hoy),
  }));
}

/** Vigencia de una versión aprobada: hasta 12 meses después de su aprobación. */
export const venceProcedimiento = (aprobadaEn: string) => sumarMesesIso(diaEc(aprobadaEn), MESES_VIGENCIA_PROCEDIMIENTO);

// ── Estado de PO 21 ──
export interface DatosPO21 {
  /** Versiones aprobadas (sst_document_versions); la vigente es la de número más alto. */
  versiones: VersionEppMin[];
  /** Hay un borrador sin aprobar: el procedimiento sin ninguna versión, o cambiado después de la última aprobación. */
  borrador: boolean;
  matriz: Pick<FilaMatriz, 'position_id'>[];
  puestos: PuestoMin[];
  trabajadores: TrabajadorMin[];
}
export interface ResultadoPO21 { estado: EstadoItem; resumen: string; trazas: Traza[]; usaModulo: boolean }

const estadoTraza = (ok: boolean | null): Traza['estado'] => (ok === null ? 'por_validar' : ok ? 'cumple' : 'no_cumple');

/** La versión aprobada vigente: la de número más alto. */
export const ultimaAprobada = <T extends VersionEppMin>(vs: T[]): T | null =>
  vs.reduce<T | null>((u, v) => (!u || v.version > u.version ? v : u), null);

/** ¿El borrador tiene algo sin aprobar? Sin versión aprobada, o guardado después de la última aprobación. */
export const borradorSinAprobar = (p: Pick<FilaProcedimientoEpp, 'updated_at'> | null, ultima: VersionEppMin | null) =>
  !!p && (!ultima || p.updated_at > ultima.approved_at);

export function resultadoPO21(d: DatosPO21, hoy: Date = new Date()): ResultadoPO21 {
  const hoyI = hoyIso(hoy);
  const aprobada = ultimaAprobada(d.versiones);
  const vence = aprobada ? venceProcedimiento(aprobada.approved_at) : null;
  const vigente = !!vence && vence >= hoyI;
  const borrador = d.borrador;
  const cob = coberturaMatriz(d.puestos, d.trabajadores, d.matriz, hoy).filter(c => c.trabajadores > 0);
  const sinMatriz = cob.filter(c => c.filas === 0);
  const recientes = sinMatriz.filter(c => c.diasDesdeCreacion <= DIAS_GRACIA_PUESTO_NUEVO);
  const atrasados = sinMatriz.filter(c => c.diasDesdeCreacion > DIAS_GRACIA_PUESTO_NUEVO);
  const usaModulo = d.versiones.length > 0 || d.matriz.length > 0 || borrador;

  const estado: EstadoItem = !usaModulo ? 'pendiente'
    : vigente && sinMatriz.length === 0 ? 'cumple'
      : atrasados.length === 0 && (vigente || borrador) ? 'en_proceso'
        : 'no_cumple';

  const textoProc = vigente ? `El procedimiento tiene la versión ${aprobada!.version} aprobada y firmada el ${fechaEc(aprobada!.approved_at)} (vigente hasta el ${fechaEc(vence)}).`
    : aprobada ? `La versión ${aprobada.version} aprobada el ${fechaEc(aprobada.approved_at)} venció el ${fechaEc(vence)}${borrador ? '; hay cambios en el borrador sin aprobar' : ''}.`
      : borrador ? 'El procedimiento está en borrador: falta aprobarlo con la firma del responsable de SSO.'
        : 'El procedimiento no tiene una versión aprobada.';
  const nombres = (cs: CoberturaPuesto[]) => cs.slice(0, 5).map(c => c.puesto.name).join(', ') + (cs.length > 5 ? ` y ${cs.length - 5} más` : '');
  const textoMatriz = cob.length === 0 ? 'No hay puestos con trabajadores activos.'
    : sinMatriz.length === 0 ? (cob.length === 1 ? 'El puesto con trabajadores activos tiene su EPP en la matriz.'
      : `Los ${plural(cob.length, 'puesto', 'puestos')} con trabajadores activos tienen su EPP en la matriz.`)
      : [atrasados.length ? `Sin matriz desde hace más de ${DIAS_GRACIA_PUESTO_NUEVO} días: ${nombres(atrasados)}.` : '',
        recientes.length ? `Puestos nuevos sin matriz (dentro de los ${DIAS_GRACIA_PUESTO_NUEVO} días): ${nombres(recientes)}.` : ''].filter(Boolean).join(' ');
  const resumen = usaModulo ? `${textoProc} ${textoMatriz}`
    : 'Para cumplir PO 21: arma la matriz de EPP por puesto y aprueba el procedimiento de adquisición de EPP con la firma del responsable de SSO (pantalla de EPP).';

  const trazas: Traza[] = [
    { metodo: 'epp_po21', norma: NORMA_PO21, articulo_o_tabla: 'PO 21: procedimiento aprobado y firmado (vigencia de 12 meses)', umbral: 1, valor: vigente ? 1 : 0,
      unidad: 'versión vigente', estado: estadoTraza(usaModulo ? vigente : null), fuente_doc: FUENTE_DOC, nota: `${textoProc} ${NOTA_PLAZOS_PO21}` },
    { metodo: 'epp_po21', norma: NORMA_PO21, articulo_o_tabla: 'PO 21: matriz de EPP de cada puesto con trabajadores activos', umbral: cob.length,
      valor: cob.length - sinMatriz.length, unidad: 'puestos con matriz', estado: estadoTraza(usaModulo ? sinMatriz.length === 0 : null), fuente_doc: FUENTE_DOC, nota: textoMatriz },
  ];
  return { estado, resumen, trazas, usaModulo };
}

// ── Contenido congelado de una versión (texto + matriz anexa) y hash ──
export interface FilaMatrizCongelada { puesto: string; epp: string; especificacion: string | null; reposicion_meses: number | null; observacion: string | null }
/** Lo que se aprueba y firma (sst_document_versions.snapshot): la base exige procedimiento_epp_id, el texto guardado y la matriz. */
export interface ContenidoProcedimiento {
  tipo: 'procedimiento_adquisicion_epp';
  procedimiento_epp_id: string;
  empresa: string;
  texto: string;
  matriz: FilaMatrizCongelada[];
}

export function contenidoProcedimiento(p: Pick<FilaProcedimientoEpp, 'id' | 'body'>, empresa: string, matriz: FilaMatriz[],
  puestos: Pick<PuestoMin, 'id' | 'name'>[], catalogo: Pick<EppMin, 'id' | 'name'>[]): ContenidoProcedimiento {
  const nombrePuesto = new Map(puestos.map(x => [x.id, x.name]));
  const nombreEpp = new Map(catalogo.map(e => [e.id, e.name]));
  const filas = matriz.map(m => ({
    puesto: nombrePuesto.get(m.position_id) ?? '—', epp: nombreEpp.get(m.epp_id) ?? '—', especificacion: m.specification,
    reposicion_meses: m.replacement_months, observacion: m.observation,
  })).sort((a, b) => a.puesto.localeCompare(b.puesto, 'es') || a.epp.localeCompare(b.epp, 'es'));
  return { tipo: 'procedimiento_adquisicion_epp', procedimiento_epp_id: p.id, empresa, texto: p.body, matriz: filas };
}
export const hashProcedimiento = (c: ContenidoProcedimiento): Promise<string> => sha256Hex(canonico(c));

export function faltantesParaAprobar(p: Pick<FilaProcedimientoEpp, 'body'>, matriz: unknown[]): string[] {
  const f: string[] = [];
  if (!p.body.trim()) f.push('Escribe el texto del procedimiento (puedes partir del texto base).');
  if (matriz.length === 0) f.push('Agrega al menos un EPP a la matriz por puesto.');
  return f;
}

// ── Checklist ──
export function decisionPO21(existente: EstadoGuardadoChecklist | undefined, r: Pick<ResultadoPO21, 'estado' | 'usaModulo'>): EstadoItem | null {
  if (!r.usaModulo) return null;
  if (existente?.estado === 'no_aplica') return null;
  if (esManual(existente)) return null;
  if (existente?.estado === r.estado && existente.auto_source === FUENTE_EPP_PO21) return null;
  if (!existente && r.estado === 'pendiente') return null;
  return r.estado;
}

export function presentacionPO21(existente: EstadoGuardadoChecklist | undefined, r: ResultadoPO21): TrazaItemPlan {
  const manual = esManual(existente) && existente!.estado !== r.estado;
  return { estado: r.estado, resumen: r.resumen, trazas: r.trazas, manualRespetado: manual, estadoManual: manual ? existente!.estado : null,
    modulo: MODULO_PO21, fuente: FUENTE_EPP_PO21 };
}

/** Lee lo necesario (null si la base no tiene la M51 o no hay permiso). */
export async function cargarDatosPO21(companyId: string): Promise<DatosPO21 | null> {
  const [{ data: versiones, error: e1 }, { data: matriz, error: e2 }, { data: puestos, error: e3 }, { data: trabajadores, error: e4 },
    { data: procs, error: e5 }] = await Promise.all([
    supabase.from('sst_document_versions').select('version, approved_at').eq('company_id', companyId).eq('doc_type', 'procedimiento_epp'),
    supabase.from('epp_position_matrix').select('position_id').eq('company_id', companyId),
    supabase.from('job_positions').select('id, name, created_at').eq('company_id', companyId),
    supabase.from('workers').select('position_id, status').eq('company_id', companyId),
    supabase.from('epp_procedures').select('updated_at').eq('company_id', companyId),
  ]);
  if (e1 || e2 || e3 || e4 || e5) return null;
  const vs = (versiones ?? []) as VersionEppMin[];
  const proc = ((procs ?? []) as Pick<FilaProcedimientoEpp, 'updated_at'>[])[0] ?? null;
  return { versiones: vs, borrador: borradorSinAprobar(proc, ultimaAprobada(vs)), matriz: (matriz ?? []) as DatosPO21['matriz'],
    puestos: (puestos ?? []) as PuestoMin[], trabajadores: (trabajadores ?? []) as TrabajadorMin[] };
}

export async function syncEppPO21(companyId: string, hoy: Date = new Date()): Promise<Record<string, TrazaItemPlan>> {
  if (!companyId) return {};
  const datos = await cargarDatosPO21(companyId);
  if (!datos) return {};
  const r = resultadoPO21(datos, hoy);
  const { data: existentes } = await supabase.from('checklist_item_estados').select('item_id, estado, auto_source')
    .eq('company_id', companyId).eq('item_id', ITEM_PO21);
  const existente = ((existentes ?? []) as EstadoGuardadoChecklist[])[0];
  const d = decisionPO21(existente, r);
  if (d) {
    const { error } = await supabase.from('checklist_item_estados').upsert(
      { company_id: companyId, item_id: ITEM_PO21, estado: d, auto_source: FUENTE_EPP_PO21, updated_at: new Date().toISOString() },
      { onConflict: 'company_id,item_id' });
    if (error) console.error('syncEppPO21', error);
  }
  const guardado = d ? { item_id: ITEM_PO21, estado: d, auto_source: FUENTE_EPP_PO21 } : existente;
  return { [ITEM_PO21]: presentacionPO21(guardado, r) };
}

// "Usar cálculo automático": quita el estado manual y escribe el cálculo. Nunca sobre "No aplica".
export async function aplicarCalculoEppPO21(companyId: string, estado: EstadoItem): Promise<void> {
  const { data, error } = await supabase.from('checklist_item_estados').select('estado').eq('company_id', companyId).eq('item_id', ITEM_PO21).maybeSingle();
  if (error) throw new Error(`No se pudo leer el estado de PO 21: ${error.message}`);
  if ((data as { estado: string } | null)?.estado === 'no_aplica') throw new Error('PO 21 está marcado como "No aplica": no se cambia.');
  const { error: e2 } = await supabase.from('checklist_item_estados').upsert(
    { company_id: companyId, item_id: ITEM_PO21, estado, auto_source: FUENTE_EPP_PO21, updated_at: new Date().toISOString() },
    { onConflict: 'company_id,item_id' });
  if (e2) throw new Error(e2.code === '42501' ? 'No tienes permiso para cambiar el Checklist.' : `No se pudo aplicar el cálculo en PO 21: ${e2.message}`);
}
